import React, { useEffect, useState, useCallback } from "react";
import {
    Search,
    Download,
    Eye,
    Edit,
    MoreHorizontal,
    Trash2,
    Power,
    Copy
} from "lucide-react";

/**
 * Fonction pour transformer une chaîne en slug (URL friendly)
 */
const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // Supprime les accents
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, '-') // Remplace les espaces par des tirets
        .replace(/[^\w-]+/g, '') // Supprime les caractères spéciaux
        .replace(/--+/g, '-'); // Évite les tirets consécutifs
};

/**
 * Mappe le type de table (Int) vers son nom pour l'URL
 */
const getTableTypeString = (type) => {
    const t = parseInt(type);
    if (t === 1) return 'table';
    if (t === 2) return 'takeout';
    if (t === 3) return 'client';
    return 'table';
};
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { base_api_url, base_ui_url } from "../../../environment.jsx";
import SessionControl from "../../components/SessionControl/sessioncontrol";
import CheckRestauSelected from "../../components/SessionControl/checkrestauselected";
import { all_routes } from "../../../Router/all_routes";
import Table from "../../../core/pagination/datatable";
import AddTables from "../../../core/modals/tables/addtable";
import QRNav from "../../components/QRNav/QRNav";
import './Tables.css';

/**
 * Composant Clients - Gestion des QR Codes Clients.
 * Permet de visualiser, créer et gérer les QR codes clients du restaurant.
 */
const Clients = () => {
    const MySwal = withReactContent(Swal);
    const route = all_routes;

    // États locaux
    const [tables, setTables] = useState([]);
    const [chargement, setChargement] = useState(false);
    const [recherche, setRecherche] = useState("");
    const [filtreStatut, setFiltreStatut] = useState("Toutes");
    const [utilisateur] = useState(JSON.parse(localStorage.getItem("user")) || null);
    const [selectionnees, setSelectionnees] = useState([]);
    const [showQRDrawer, setShowQRDrawer] = useState(false);
    const [selectedTableForQR, setSelectedTableForQR] = useState(null);

    // Modales
    const [showAddTableModal, setShowAddTableModal] = useState(false);
    const [menuActionsOuvert, setMenuActionsOuvert] = useState(null);

    /**
     * Charge les tables depuis l'API.
     */
    const recupererTables = useCallback(async () => {
        setChargement(true);
        const token = utilisateur?.access_token;
        const restoId = utilisateur?.restoId;

        try {
            const url = `${base_api_url}api/table/${restoId}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.status === 200 || response.status === 201) {
                const tablesData = response.data.table || [];
                console.log("Tables récupérées :", tablesData);
                setTables(tablesData);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des tables:", error);
        } finally {
            setChargement(false);
        }
    }, [utilisateur?.access_token, utilisateur?.restoId]);


    useEffect(() => {
        recupererTables();
    }, [recupererTables]);

    /**
     * Gère le téléchargement d'un QR code individuel.
     */
    const telechargerQRCode = async (table) => {
        try {
            const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
            const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
            const restaurantName = freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || 'restaurant';
            const restaurantSlug = slugify(restaurantName);
            const typeStr = getTableTypeString(table.type_table);
            const qrData = `${base_ui_url}${restaurantSlug}/${typeStr}/homemenu?r=${utilisateur?.restoId}&t=${table.code_qr}`;
            const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrData)}`;

            const response = await fetch(qrApiUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `QR_Client_${table.numero_table}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Erreur téléchargement QR:", error);
        }
    };

    /**
     * Télécharge tous les QR codes avec un délai pour ne pas saturer.
     */
    const telechargerTout = async () => {
        if (tables.length === 0) return;

        MySwal.fire({
            text: 'Vos QR codes vont être téléchargés un par un.',
            allowOutsideClick: false,
            didOpen: () => MySwal.showLoading()
        });

        for (let i = 0; i < tables.length; i++) {
            await telechargerQRCode(tables[i]);
            if (i < tables.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        MySwal.close();
    };

    /**
     * Copie le QR code dans le presse-papier.
     */
    const copierQRCode = async (table) => {
        try {
            const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
            const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
            const restaurantName = freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || 'restaurant';
            const restaurantSlug = slugify(restaurantName);
            const typeStr = getTableTypeString(table.type_table);
            const qrData = `${base_ui_url}${restaurantSlug}/${typeStr}/homemenu?r=${utilisateur?.restoId}&t=${table.code_qr}`;

            await navigator.clipboard.writeText(qrData);

            MySwal.fire({
                icon: "success",
                text: "Lien du QR Code copié !",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        } catch (error) {
            console.error("Erreur lors de la copie du lien:", error);
            MySwal.fire({
                icon: "error",
                text: "Impossible de copier le lien.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };


    // Filtrage des données (statut: 'actif' ou 'inactif')
    const donneesFiltrees = tables.filter(item => {
        // Filter by type: only QR Client tables (type 3)
        if (item.type_table !== 3) return false;

        const matchesSearch = item.numero_table.toString().includes(recherche) ||
            item.code_qr.toLowerCase().includes(recherche.toLowerCase());

        if (filtreStatut === "Toutes") return matchesSearch;
        if (filtreStatut === "Actives") return matchesSearch && item.statut?.toLowerCase() === "actif";
        if (filtreStatut === "Inactives") return matchesSearch && item.statut?.toLowerCase() === "inactif";

        return matchesSearch;
    }).map((item, index) => {
        const restoId = utilisateur?.restoId;
        const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
        const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
        const restaurantName = freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || 'restaurant';
        const restaurantSlug = slugify(restaurantName);
        const typeStr = getTableTypeString(item.type_table);
        return {
            ...item,
            key: index + 1,
            id: item.id_table,
            url: `${base_ui_url}${restaurantSlug}/${typeStr}/homemenu?r=${restoId}&t=${item.code_qr}`
        };
    });

    // Définition des colonnes de la table
    const colonnes = [
        {
            title: "N°",
            dataIndex: "numero_table",
            render: (num) => <span style={{ fontWeight: 600, color: '#111827' }}>{num.toString().padStart(2, '0')}</span>,
            sorter: (a, b) => a.numero_table - b.numero_table,
        },
        {
            title: "Statut",
            dataIndex: "statut",
            render: (statut) => {
                const isActif = statut?.toLowerCase() === "actif";
                return (
                    <span className={isActif ? "status-badge-actif" : "status-badge-inactif"}>
                        {isActif ? "Actif" : "Inactif"}
                    </span>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: 200,
            render: (_, record) => (
                <div className="action-buttons-tables">
                    <button
                        className="btn-table-action btn-view"
                        title="Voir le QR code"
                        onClick={() => {
                            setSelectedTableForQR(record);
                            setShowQRDrawer(true);
                        }}
                    >
                        <Eye size={16} />
                    </button>
                    <button
                        className="btn-table-action btn-download"
                        title="Télécharger"
                        onClick={() => telechargerQRCode(record)}
                    >
                        <Download size={16} />
                    </button>
                    <button
                        className="btn-table-action btn-edit"
                        title="Modifier"
                        onClick={() => {
                            setShowAddTableModal(true);
                        }}
                    >
                        <Edit size={16} />
                    </button>
                    <div className="dropdown-actions-container">
                        <button
                            className="btn-table-action btn-more"
                            onClick={() => setMenuActionsOuvert(menuActionsOuvert === record.id_table ? null : record.id_table)}
                        >
                            <MoreHorizontal size={20} />
                        </button>
                        {menuActionsOuvert === record.id_table && (
                            <div className="dropdown-actions-menu" style={{
                                position: 'absolute', top: '100%', right: 0,
                                background: 'white', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                                borderRadius: '8px', zIndex: 100, minWidth: '130px',
                                border: '1px solid #f3f4f6', marginTop: '4px',
                                overflow: 'hidden'
                            }}>
                                <button className="d-flex align-items-center gap-2 px-3 py-2 w-100 text-start border-0 bg-transparent" style={{ fontSize: '13px', color: '#374151' }}>
                                    <Power size={14} /> Désactiver
                                </button>
                                <div style={{ height: '1px', backgroundColor: '#f3f4f6' }}></div>
                                <button className="d-flex align-items-center gap-2 px-3 py-2 w-100 text-start border-0 bg-transparent text-danger" style={{ fontSize: '13px' }}>
                                    <Trash2 size={14} /> Supprimer
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="page-wrapper">
            <SessionControl storageKey="user" propsup="email" redirectTo={route.signin} />
            <CheckRestauSelected storageKey="user" redirectTo={route.Restaurants} />

            <div className="tables-page">
                {/* BARRE DE NAVIGATION ET EN-TÊTE UNIFIE */}
                <QRNav
                    activeTabId="clients"
                    subtitle="Générez et gérez les QR Codes personnalisés pour vos clients"
                    addButtonText="Ajouter un QR Client"
                    onAddClick={() => setShowAddTableModal(true)}
                />

                {/* FILTRES */}
                <div className="filters-row-tables">
                    <div className="filters-left">
                        <div className="search-wrapper-tables">
                            <Search className="search-icon-tables" size={18} />
                            <input
                                type="text"
                                className="search-input-tables"
                                placeholder="Rechercher un QR client..."
                                value={recherche}
                                onChange={(e) => setRecherche(e.target.value)}
                            />
                        </div>
                        <div className="status-pills">
                            {["Toutes", "Actives", "Inactives"].map(s => (
                                <button
                                    key={s}
                                    className={`status-pill ${filtreStatut === s ? 'active' : ''}`}
                                    onClick={() => setFiltreStatut(s)}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="bulk-actions-wrapper">
                        {selectionnees.length > 0 ? (
                            <div className="d-flex gap-2">
                                <button className="btn-bulk btn-disable">
                                    <Power size={14} />
                                    Désactiver ({selectionnees.length})
                                </button>
                                <button className="btn-bulk btn-download-bulk">
                                    <Download size={14} />
                                    Télécharger ({selectionnees.length})
                                </button>
                                <button className="btn-bulk btn-delete">
                                    <Trash2 size={14} />
                                    Supprimer ({selectionnees.length})
                                </button>
                            </div>
                        ) : (
                            <button className="btn-download-all" onClick={telechargerTout}>
                                <Download size={16} />
                                Tout télécharger
                            </button>
                        )}
                    </div>
                </div>

                {/* LISTE DES TABLES */}
                <div className="tables-list-container">
                    <div className="table-responsive">
                        <Table
                            columns={colonnes}
                            dataSource={donneesFiltrees}
                            loading={chargement}
                            rowSelection={{
                                type: 'checkbox',
                                selectedRowKeys: selectionnees,
                                onChange: (selectedRowKeys) => {
                                    setSelectionnees(selectedRowKeys);
                                }
                            }}
                        />
                    </div>
                </div>
                {/* MODALE AJOUT/MODIF */}
                {showAddTableModal && (
                    <AddTables
                        show={showAddTableModal}
                        onClose={() => setShowAddTableModal(false)}
                        onFormSubmit={() => {
                            setShowAddTableModal(false);
                            recupererTables();
                        }}
                        defaultType={3}
                    />
                )}
                {/* DRAWER QR CODE */}
                {showQRDrawer && selectedTableForQR && (
                    <>
                        <div className={`etiquette-drawer show`} style={{ zIndex: 2000, width: 420, maxWidth: '100vw', right: 0, top: 0, position: 'fixed', height: '100vh', background: '#fff', boxShadow: '-2px 0 16px rgba(0,0,0,0.08)', overflowY: 'auto', transition: 'right 0.3s' }}>
                            <div className="drawer-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '24px 24px 12px 24px' }}>
                                <h4 style={{ margin: 0 }}>QR Code Client - {selectedTableForQR.numero_table}</h4>
                                <button className="drawer-close-btn" style={{ fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setShowQRDrawer(false)}>×</button>
                            </div>
                            <div style={{ padding: '0 24px 24px 24px' }}>
                                <div style={{ margin: '18px 0 8px 0', fontWeight: 500, fontSize: 15 }}>Statut {selectedTableForQR.statut && (
                                    <span style={{ marginLeft: 8, padding: '2px 12px', borderRadius: 12, background: selectedTableForQR.statut.toLowerCase() === 'actif' ? '#22c55e' : '#e5e7eb', color: selectedTableForQR.statut.toLowerCase() === 'actif' ? '#fff' : '#374151', fontSize: 13 }}>{selectedTableForQR.statut.charAt(0).toUpperCase() + selectedTableForQR.statut.slice(1)}</span>
                                )}</div>
                                <div style={{ border: '1px dashed #e5e7eb', borderRadius: 12, padding: 24, margin: '18px 0', textAlign: 'center', background: '#fafbfc' }}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(selectedTableForQR.url)}`}
                                        alt={`QR Code Client ${selectedTableForQR.numero_table}`}
                                        className="img-fluid border rounded"
                                        style={{ maxWidth: '300px', height: 'auto', margin: '0 auto' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <div style={{ color: '#6b7280', fontSize: 15 }}>Restaurant</div>
                                    <div style={{ fontWeight: 500 }}>
                                        {(() => {
                                            const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
                                            const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
                                            return freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || '-';
                                        })()}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 }}>
                                    <div style={{ color: '#6b7280', fontSize: 15 }}>Créée le</div>
                                    <div style={{ fontWeight: 500 }}>{selectedTableForQR.created_at ? new Date(selectedTableForQR.created_at).toLocaleDateString() : '-'}</div>
                                </div>
                                <div className="drawer-footer-actions">
                                    <button
                                        className="drawer-btn drawer-btn-primary"
                                        onClick={() => telechargerQRCode(selectedTableForQR)}
                                    >
                                        <Download size={16} style={{ marginRight: 6 }} />
                                        Télécharger
                                    </button>
                                    <button
                                        className="drawer-btn drawer-btn-copy"
                                        onClick={() => copierQRCode(selectedTableForQR)}
                                    >
                                        <Copy size={16} style={{ marginRight: 6 }} />
                                        Copier
                                    </button>
                                    <button
                                        className="drawer-btn drawer-btn-secondary"
                                        onClick={() => window.print()}
                                    >
                                        <span style={{ marginRight: 6, display: 'flex' }}><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="6" y="9" width="12" height="7" rx="2" /><path d="M6 17v2a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" /><path d="M6 9V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" /></svg></span>
                                        Imprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="drawer-backdrop show" style={{ zIndex: 1999 }} onClick={() => setShowQRDrawer(false)}></div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Clients;
